import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { BarChart3, RefreshCw, AlertTriangle } from 'lucide-react';
import { billingApi } from '../../../api/client';
import type { BillingUsage } from '../../../api/client';
import { usePermissions } from '../../../hooks/usePermissions';

// ── Constants ──────────────────────────────────────────────────────────────────

const USAGE_LABELS: Record<string, string> = {
  ai_agent_execution:        'AI Agent Executions',
  recommendation_generation: 'Recommendations Generated',
  realtime_alert:            'Realtime Alerts',
  api_call:                  'API Calls',
  backtest_run:              'Backtest Runs',
  calibration_run:           'Calibration Runs',
  data_export:               'Data Exports',
};

// ── Main component ─────────────────────────────────────────────────────────────

export function UsageSettingsPage() {
  const { can } = usePermissions();
  const canView  = can('billing.view');

  const [usage,      setUsage]      = useState<BillingUsage | null>(null);
  const [isLoading,  setIsLoading]  = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  useEffect(() => {
    if (!canView) return;
    setIsLoading(true);
    billingApi.getUsage()
      .then(setUsage)
      .catch((err) => setError((err as Error).message ?? 'Failed to load usage data'))
      .finally(() => setIsLoading(false));
  }, [canView]);

  if (!canView) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        <p className="text-sm text-gray-500">You don't have permission to view usage metrics.</p>
      </div>
    );
  }

  const period = usage?.period
    ? new Date(usage.period).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '';
  const entries = Object.entries(usage?.byType ?? {}).filter(([, v]) => v > 0);

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">Usage</h2>
        <p className="text-sm text-gray-500 mt-1">
          Monitor your organization's platform usage for the current billing period.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading usage…
        </div>
      ) : (
        <>
          {/* Period / total summary */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Usage for {period || 'current period'}
            </p>
            {usage && (
              <span className="text-xs text-gray-500">
                Total:{' '}
                <span className="text-gray-300 font-medium">
                  {usage.total.toLocaleString()} operations
                </span>
              </span>
            )}
          </div>

          {entries.length === 0 ? (
            <div className="bg-surface-1 border border-surface-4 rounded-lg p-8 text-center">
              <BarChart3 className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No usage recorded this period</p>
              <p className="text-xs text-gray-600 mt-1">
                Operations will appear here once your organization starts using the platform.
              </p>
            </div>
          ) : (
            <div className="bg-surface-1 border border-surface-4 rounded-lg overflow-hidden">
              {entries.map(([type, count], i) => {
                const maxVal = Math.max(...entries.map(([, v]) => v));
                const pct    = maxVal > 0 ? (count / maxVal) * 100 : 0;
                return (
                  <div
                    key={type}
                    className={clsx(
                      'px-4 py-3 flex items-center gap-4',
                      i < entries.length - 1 && 'border-b border-surface-4/50',
                    )}
                  >
                    <div className="w-48 text-xs text-gray-400 flex-shrink-0">
                      {USAGE_LABELS[type] ?? type}
                    </div>
                    <div
                      className="flex-1 h-1.5 bg-surface-3 rounded-full overflow-hidden"
                      role="progressbar"
                      aria-valuenow={count}
                      aria-valuemax={maxVal}
                      aria-label={USAGE_LABELS[type] ?? type}
                    >
                      <div
                        className="h-full bg-brand rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="w-16 text-right text-xs text-gray-300 font-medium flex-shrink-0">
                      {count.toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* All-zero fallback usage summary when byType has entries but all are 0 */}
          {usage && usage.total === 0 && Object.keys(usage.byType).length > 0 && (
            <p className="text-xs text-gray-600 text-center">
              All recorded operations have a count of zero this period.
            </p>
          )}
        </>
      )}
    </div>
  );
}
